/**
 * Zod schema for the Insurance Contribution create/edit form (Phase 26 / PAY2-03, be-207).
 *
 * Business rules (be-207 §Plan 26-03 Insurance):
 *   - kind: "MEDICAL" | "LIFE" | "OTHER"
 *   - Per-side XOR: each side (employee, employer) accepts pct OR amount — never both, never neither is ok
 *   - Employee side: employeeContributionPct (0..1, scale 4) OR employeeContributionAmount (≥0, scale 3)
 *   - Employer side: employerContributionPct (0..1, scale 4) OR employerContributionAmount (≥0, scale 3)
 *   - effectiveFrom: required ISO date string
 *   - effectiveTo: optional nullable
 *
 * Error code on XOR mismatch: 400 INSURANCE_CONTRIBUTION_PCT_AMOUNT_BOTH_SET.
 * FE enforces per-side XOR upfront via two separate .refine() calls.
 *
 * Test 4 design decision: allowing all four contribution fields to be absent
 * (kind + effectiveFrom only) — this represents a tracked-but-not-deducted
 * contribution. Backend allows this per be-207 §notes (empty sides are valid).
 *
 * Exported for both the form component and contract tests.
 * TODO: derive from generated schema after CC-142 openapi regen (Plan 26-05).
 */
import { z } from "zod";

export const insuranceContributionFormSchema = z
  .object({
    kind: z.enum(["MEDICAL", "LIFE", "OTHER"], {
      invalid_type_error: "Kind must be MEDICAL, LIFE, or OTHER",
    }),
    /** Employee side pct: 0..1 fraction, scale 4 (e.g. 0.025 = 2.5%) */
    employeeContributionPct: z.number().min(0).max(1).optional(),
    /** Employee side fixed amount: non-negative, scale 3 */
    employeeContributionAmount: z.number().nonnegative().optional(),
    /** Employer side pct: 0..1 fraction, scale 4 */
    employerContributionPct: z.number().min(0).max(1).optional(),
    /** Employer side fixed amount: non-negative, scale 3 */
    employerContributionAmount: z.number().nonnegative().optional(),
    effectiveFrom: z.string().min(1, "Effective from date is required"),
    effectiveTo: z.string().nullable().optional(),
  })
  .refine(
    (v) => !(v.employeeContributionPct !== undefined && v.employeeContributionAmount !== undefined),
    {
      message: "Employee side: set either a percentage or a fixed amount — not both",
      path: ["employeeContributionPct"],
    },
  )
  .refine(
    (v) => !(v.employerContributionPct !== undefined && v.employerContributionAmount !== undefined),
    {
      message: "Employer side: set either a percentage or a fixed amount — not both",
      path: ["employerContributionPct"],
    },
  );

export type InsuranceContributionFormValues = z.infer<typeof insuranceContributionFormSchema>;
